/**
 * Machine L1 Hardware facet — Live telemetry Soft≠Live (§5.2).
 * Absorbs former L0 instrument: presence · TF · ports/pins when board profile Live.
 * Honest empty on 409 — IP / GPIO / board profile are never invented.
 * Pin learn / bind write surface lives on Edge Board desk (deep-link below).
 */
import { useMemo } from "react";
import { html, Ico, cmcPlainOf, go, stop } from "./ui.js";
import { path, useApi } from "./api.js";
import { fleetDeviceRegisterOf } from "./fleet-device-desk.js";
import { nearestLiveMast } from "./device-link-live.js";
import { poeLiveFromTelemetry } from "./board-telemetry-adapter.js";
import {
  storageCardViewOf,
  boardPortsViewOf,
  boardLoopViewOf,
  pinWakeOf,
} from "./edgeos-live.js";
import { presenceDiagnoseView, presenceSuspectIsProblem } from "./fleet-live.js";
import { safeBoardPath } from "./board-api.js";
import { explainBoardError } from "./board-seat-adapter.js";

function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    return safeBoardPath(op, opts) || "";
  }
}

function loopOnCount(loops) {
  if (!loops || !Array.isArray(loops.items)) return 0;
  return loops.items.filter((x) => x.tone === "on").length;
}

function fmtMb(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n >= 1024) return (n / 1024).toFixed(1) + " GB";
  return String(Math.round(n)) + " MB";
}

function fmtAge(s) {
  if (s == null || !Number.isFinite(s)) return "—";
  if (s < 60) return Math.floor(s) + "s";
  if (s < 3600) return Math.floor(s / 60) + "m";
  return Math.floor(s / 3600) + "h";
}

function isBoardProfileUnknown(err) {
  const s = String(err || "").toLowerCase();
  return /board_profile_unknown|409/.test(s);
}

/** Power line from the telemetry frame already loaded. Kind never becomes watts. */
function powerLineOf(poe, loading, error) {
  const fields = poe && poe.fields ? poe.fields : null;
  const watts = fields && fields.watts != null ? String(fields.watts) : "";
  const classN = fields && fields.classN != null ? String(fields.classN) : "";
  const vin = fields && fields.vin != null ? String(fields.vin) : "";
  let honesty = "no measured power in this frame";
  if (loading) honesty = "reading board telemetry";
  else if (error) honesty = "board telemetry unreadable · power stays blank";
  else if (!poe || !poe.fresh) honesty = "board frame is not fresh · power stays blank";
  else if (fields) honesty = "measured on the live PoE frame · not guessed from the board kind";
  const label = watts !== "" ? (watts + " W")
    : classN !== "" ? ("class " + classN)
    : vin !== "" ? (vin + " V")
    : "—";
  return { watts, classN, vin, honesty, label };
}

/** Soft≠Live: Hardware panel for one Live machine id. */
export function MachineHardwarePanel({ deviceId, toast }) {
  const did = String(deviceId || "").trim();
  const teleUrl = did ? safePath("get__devices_p_board-telemetry", { pathParams: [did] }) : null;
  const loopUrl = did ? safePath("get__devices_p_board-loop", { pathParams: [did] }) : null;
  const cardUrl = did ? safePath("get__devices_p_storage_card", { pathParams: [did] }) : null;
  const portsUrl = did ? safePath("get__devices_p_ports", { pathParams: [did] }) : null;
  const pinsUrl = did ? safePath("get__devices_p_pins", { pathParams: [did] }) : null;
  const diagUrl = did ? safePath("get__devices_p_presence-diagnose", { pathParams: [did] }) : null;

  const devicesApi = useApi(did ? path("get__devices") : null);
  const register = useMemo(() => fleetDeviceRegisterOf(devicesApi.data), [devicesApi.data]);
  const self = useMemo(
    () => register.find((row) => row.id === did) || null,
    [register, did],
  );
  const mast = useMemo(() => {
    if (!self) {
      return {
        label: "—",
        km: null,
        site: "",
        honesty: devicesApi.loading ? "reading the register" : "not in this register",
      };
    }
    return nearestLiveMast(self, register);
  }, [self, register, devicesApi.loading]);

  const teleLive = useApi(teleUrl);
  const poe = useMemo(() => poeLiveFromTelemetry(teleLive.data || null), [teleLive.data]);
  const power = powerLineOf(poe, !!teleLive.loading, teleLive.error);
  const loopLive = useApi(loopUrl);
  const cardLive = useApi(cardUrl);
  const portsLive = useApi(portsUrl);
  const pinsLive = useApi(pinsUrl);
  const diagLive = useApi(diagUrl);

  const card = useMemo(() => storageCardViewOf(cardLive.data), [cardLive.data]);
  const ports = useMemo(() => boardPortsViewOf(portsLive.data), [portsLive.data]);
  const loops = useMemo(() => boardLoopViewOf(loopLive.data), [loopLive.data]);
  const diag = useMemo(() => presenceDiagnoseView(diagLive.data), [diagLive.data]);
  const seats = useMemo(() => {
    const raw = (pinsLive.data && (pinsLive.data.items || pinsLive.data.seats)) || [];
    return Array.isArray(raw) ? raw : [];
  }, [pinsLive.data]);

  const wakeN = seats.filter((s) => pinWakeOf(s) === "wake").length;
  const sleepN = seats.filter((s) => pinWakeOf(s) === "sleep").length;
  const online = !!(diag && diag.online);
  const profileUnknown = isBoardProfileUnknown(teleLive.error)
    || isBoardProfileUnknown(portsLive.error)
    || isBoardProfileUnknown(pinsLive.error);

  const reload = () => {
    teleLive.reload && teleLive.reload();
    loopLive.reload && loopLive.reload();
    cardLive.reload && cardLive.reload();
    portsLive.reload && portsLive.reload();
    pinsLive.reload && pinsLive.reload();
    diagLive.reload && diagLive.reload();
    devicesApi.reload && devicesApi.reload();
    toast && toast("Hardware re-read", "go");
  };

  if (!did) {
    return html`<p className="note">Pick a machine from the list.</p>`;
  }

  return html`<div className="machine-hw" data-cmc-m35-facet="hardware" data-device=${did}
    data-cmc-nearest-mast=${mast.label} data-cmc-nearest-home=${mast.site || ""}>
    <div className="btn-row" style=${{ flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
      <button type="button" className="btn btn--ghost btn--sm" onClick=${reload}>
        <${Ico} d="refresh" w=${13}/> Re-read hardware</button>
      <button type="button" className="btn btn--ghost btn--sm"
        title="Pin learn / bind / Board Ask live on Edge Board desk"
        onClick=${(e) => {
          stop(e);
          go("/edge/board?device=" + encodeURIComponent(did));
        }}>
        <${Ico} d="chip" w=${13}/> Pin map · Edge Board</button>
    </div>

    <div className="machine-hw__kpis">
      <div className="machine-hw__kpi" data-on=${online ? "1" : "0"}>
        <span>Presence</span>
        <b>${online ? "Online" : "Quiet"}</b>
        <em>${diag && diag.ages ? ("hb " + fmtAge(diag.ages.heartbeat)) : "Presence check"}</em>
      </div>
      <div className="machine-hw__kpi" data-on=${power.watts || power.classN || power.vin ? "1" : "0"}
        data-cmc-power="1" data-cmc-poe-watts=${power.watts} data-cmc-poe-class=${power.classN}
        data-cmc-poe-vin=${power.vin} data-cmc-poe-honesty=${power.honesty}>
        <span>Power</span>
        <b>${power.label}</b>
        <em>${power.honesty}</em>
      </div>
      <div className="machine-hw__kpi" data-on=${card && card.present ? "1" : "0"}>
        <span>TF</span>
        <b>${card ? (card.mounted ? "Mount" : card.present ? "Present" : "Empty") : "—"}</b>
        <em>${card ? ((card.fs && card.fs !== "—" ? card.fs + " · " : "") + fmtMb(card.freeMb)) : (cardLive.error ? explainBoardError(cardLive.error) : "cold store")}</em>
      </div>
      <div className="machine-hw__kpi" data-on=${!profileUnknown && seats.length ? "1" : "0"}>
        <span>Pins</span>
        <b>${profileUnknown ? "—" : (seats.length ? String(seats.length) : "—")}</b>
        <em>${profileUnknown ? "no board profile" : (wakeN + " wake · " + sleepN + " sleep" + (loops ? (" · mapped " + loops.pinMapped) : ""))}</em>
      </div>
      <div className="machine-hw__kpi" data-on=${!profileUnknown && ports.length ? "1" : "0"}>
        <span>Ports</span>
        <b>${profileUnknown ? "—" : (ports.length ? String(ports.length) : "—")}</b>
        <em>${profileUnknown ? "awaiting profile" : "Board ports"}</em>
      </div>
      <div className="machine-hw__kpi" data-on=${loops && loops.items.length ? "1" : "0"}>
        <span>Loops</span>
        <b>${loops ? String(loopOnCount(loops)) + "/" + loops.items.length : "—"}</b>
        <em>${loops && loops.aiParked ? "AI parked; loops unavailable" : (loopLive.error ? explainBoardError(loopLive.error) : "board-loop")}</em>
      </div>
    </div>

    <p className="note" data-cmc-nearest-honesty=${mast.honesty}>
      Nearest mast · ${mast.km == null ? mast.label : (mast.label + " · " + mast.km.toFixed(2) + " km")}
      · ${mast.honesty}
    </p>

    ${diagLive.error
      ? html`<p className="note">Presence · ${cmcPlainOf(explainBoardError(diagLive.error))}</p>`
      : null}
    ${diag && presenceSuspectIsProblem(diag.suspect)
      && !(loops && loops.items && loops.items.length && diag.suspect === "B0_row_stale_unknown_cause")
      ? html`<p className="note">Suspect · ${cmcPlainOf(diag.suspect)}</p>`
      : (diag && diag.suspect === "B8_http_board_live"
        ? html`<p className="note">Presence · HTTP board live${diag.ages && diag.ages.board != null ? (" · " + fmtAge(diag.ages.board)) : ""}</p>`
        : null)}

    ${profileUnknown
      ? html`<div className="machine-hw__honest">
          <b>Board profile not reported</b>
          <p>LAN / Wi-Fi / pin map stay blank until this node reports a board profile.
            Empty stays empty — IP and GPIO are never invented here.</p>
        </div>`
      : null}

    ${!profileUnknown && ports.length
      ? html`<div className="machine-hw__ports">
          ${ports.map((p) => html`
            <div key=${p.id} className="machine-hw__port" data-on=${p.link === true ? "1" : "0"}>
              <b>${p.id}</b>
              <span>${p.kind || "port"}</span>
              <em>${cmcPlainOf(p.ip || p.detail || (p.link === true ? "up" : p.link === false ? "down" : "—"))}</em>
            </div>`)}
        </div>`
      : null}

    ${loops && loops.items.length
      ? html`<div className="machine-hw__loops" data-cmc-board-loop="1">
          ${loops.items.map((x) => html`
            <div key=${x.id} className="machine-hw__loop" data-tone=${x.tone}>
              <b>${x.id}</b>
              <span>${x.value}</span>
            </div>`)}
        </div>`
      : (loopLive.error && !loopLive.data
        ? html`<p className="note">Board-loop · ${cmcPlainOf(explainBoardError(loopLive.error))}</p>`
        : null)}

    ${!profileUnknown && seats.length
      ? html`<div className="machine-hw__pins">
          ${seats.slice(0, 32).map((s, i) => {
            const wake = pinWakeOf(s);
            return html`<div key=${s.seat_id || i} className="machine-hw__pin" data-wake=${wake}>
              <b>${s.seat_id}</b>
              <span>${wake}</span>
              <em>${s.gpio != null ? ("g" + s.gpio) : "—"}</em>
            </div>`;
          })}
        </div>`
      : null}
  </div>`;
}

export default MachineHardwarePanel;
